/**
 * MyKeySection — "My Key": every confirmed workspace member creates or
 * replaces their own personal key from here. It owns and shows the key
 * value, once, right after create/replace; nowhere else on the page does.
 *
 * track:team-v2-page (spec #66501, handoff #66489). POSTs
 * /api/workspace/member-key (any confirmed member, human callers only). The
 * server always revokes any existing key first, then mints a fresh one, so
 * create and replace are the same call; `status` only changes the wording.
 * After "I've Saved It" (or a reload) the key is gone for good and the card
 * shows the existing-key row from GET /api/workspace/member-key (TeamPage
 * passes it in as `keyInfo`). Replacing uses an inline confirm — never
 * window.confirm.
 */
import { useEffect, useState } from "react";
import { getAuthHeader } from "../../lib/auth";
import { SecretField } from "./SecretField";
import { relativeTime } from "./useHelperStatus";

const URL_PATH = "/mcp/team-log/";

/** Just-created personal key. The server never returns it again. */
export interface CreatedKey {
  mcpUrl: string;
  status: "created" | "regenerated";
}

/** GET /api/workspace/member-key, normalised. Never contains the key. */
export interface KeyInfo {
  exists: boolean;
  last4: string | null;
  createdAt: string | null;
  lastUsedAt: string | null;
}

export function keyFromUrl(url: string): string {
  return url.split(URL_PATH)[1] ?? "";
}

interface MemberKeyResponse {
  mcp_url: string;
  status: "created" | "regenerated";
  error?: string;
}

interface MyKeySectionProps {
  workspaceId?: string;
  /** null until the first GET answers (loading) or while it has failed (see keyError). */
  keyInfo: KeyInfo | null;
  /** The status GET failed and there is no known status yet. */
  keyError?: boolean;
  onRetry?: () => void;
  created: CreatedKey | null;
  onCreated: (created: CreatedKey) => void;
  /** "I've Saved It" — hide the key for good and refresh the key row. */
  onSaved: () => void;
}

export function MyKeySection({ workspaceId = "default", keyInfo, keyError = false, onRetry, created, onCreated, onSaved }: MyKeySectionProps) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);

  // Leaving the page with a key that was never saved loses it for good.
  useEffect(() => {
    if (!created) return;
    const handler = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = ""; };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [created]);

  async function handleCreate() {
    setLoading(true);
    setError(null);
    try {
      const headers = await getAuthHeader(workspaceId);
      const res = await fetch("/api/workspace/member-key", {
        method: "POST",
        headers: { "Content-Type": "application/json", ...headers },
      });
      const data: MemberKeyResponse = await res.json();
      if (!res.ok) {
        setError(data.error || `HTTP ${res.status}`);
        return;
      }
      setConfirming(false);
      onCreated({ mcpUrl: data.mcp_url, status: data.status });
    } catch (err) {
      console.error("[MyKeySection] Failed to create/replace key:", err);
      setError("Network error. Please try again.");
    } finally {
      setLoading(false);
    }
  }

  // Until the status is known we must not offer Create: it replaces any existing key.
  const keyState = created ? created.status : !keyInfo ? (keyError ? "error" : "loading") : keyInfo.exists ? "existing" : "none";
  const lastUsed = relativeTime(keyInfo?.lastUsedAt);
  const createdOn = keyInfo?.createdAt ? new Date(keyInfo.createdAt).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : null;

  return (
    <section id="team-section-my-key" style={{ marginBottom: 32 }}>
      <div className="sc-section-label">My Key</div>
      <div className="tp-lede">Your personal key. Relay Helper and your AI chats use it to act as you.</div>

      <div className="tp-card">
        {keyState === "loading" && <div className="tp-small" data-testid="my-key-loading">Checking your key…</div>}

        {keyState === "error" && (
          <div className="sc-callout error" data-testid="my-key-status-error">
            Couldn’t check whether you already have a key. Try again before creating one.
            <div style={{ marginTop: 8 }}><button className="btn sm" onClick={onRetry}>Retry</button></div>
          </div>
        )}

        {keyState === "none" && (
          <>
            <div className="tp-small" style={{ marginBottom: 10 }}>
              You don’t have a key yet. Relay Helper uses it to do work as you, and AI chats you connect use it to post as you. Everyone on the team creates their own.
            </div>
            <button className="btn primary" onClick={handleCreate} disabled={loading}>
              {loading ? "Creating…" : "Create My Key"}
            </button>
          </>
        )}

        {created && (
          <>
            <div className="sc-callout warn" style={{ marginBottom: 12 }}>
              <strong>Copy this now. It won’t be shown again.</strong>{" "}
              {created.status === "regenerated"
                ? "Your previous key stopped working. Paste this one into every Mac and AI chat that used the old one."
                : "Paste it into Relay Helper before you leave this page."}
            </div>
            <div className="tp-small" style={{ marginBottom: 4 }}>Your key</div>
            <SecretField key={`k-${created.mcpUrl}`} value={keyFromUrl(created.mcpUrl)} startRevealed testId="my-key-value" />
            <div className="tp-small" style={{ margin: "8px 0 4px" }}>Your personal connection URL · for AI chats, contains your key</div>
            <SecretField key={`u-${created.mcpUrl}`} value={created.mcpUrl} testId="my-key-url" />
            <div className="tp-row">
              <div className="tp-row-label">Where to paste it</div>
              <div className="tp-small">
                <div><strong>Relay Helper</strong> · setup step 2, or Settings › Account › Replace Key</div>
                <div><strong>AI chat</strong> · paste the connection URL, see Connect an AI chat</div>
                <div><strong>Terminal</strong> · <code>relay install</code> asks for it</div>
              </div>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <button className="btn sm" onClick={onSaved}>I’ve Saved It</button>
              <span className="tp-small">Hides the key for good.</span>
            </div>
          </>
        )}

        {keyState === "existing" && (
          <>
            <div className="sc-item-row" style={{ marginBottom: 8 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10, flex: 1 }}>
                <span className="tp-dot" />
                <div>
                  <div className="sc-item-name" style={{ fontFamily: "var(--font-mono)" }}>
                    relay_wk_live_…{keyInfo?.last4 ?? "••••"}
                  </div>
                  <div className="sc-item-meta">
                    {createdOn ? `Created ${createdOn}` : "Created"}
                    {lastUsed ? ` · last used by Relay Helper ${lastUsed}` : ""}
                  </div>
                </div>
              </div>
              <span className="tp-badge">Active</span>
            </div>
            <div className="tp-small" style={{ marginBottom: 10 }}>
              Relay only shows a key once. If you lost it, create a new one. The old one stops working everywhere straight away.
            </div>
            {!confirming ? (
              <button className="btn sm" onClick={() => setConfirming(true)}>Create New Key…</button>
            ) : (
              <div className="sc-callout warn">
                <strong>Replace your key?</strong>
                <div style={{ margin: "4px 0 8px" }}>
                  Relay Helper and any AI chat using the old key stop working until you paste the new one in.
                </div>
                <div style={{ display: "flex", gap: 8 }}>
                  <button className="btn sm danger" onClick={handleCreate} disabled={loading}>
                    {loading ? "Creating…" : "Create New Key"}
                  </button>
                  <button className="btn sm" onClick={() => setConfirming(false)} disabled={loading}>Cancel</button>
                </div>
              </div>
            )}
          </>
        )}

        {error && <div className="sc-callout error" style={{ marginTop: 12 }}>{error}</div>}
      </div>
    </section>
  );
}
